import React, { useState, useEffect } from 'react';
import {
  Settings,
  Users,
  Timer,
  Plus,
  X,
  Trash2,
  Copy,
  ExternalLink,
  Eye,
  Crown,
  User,
  UserCheck,
  UserX,
  Shuffle,
  Save,
  AlertCircle,
  CheckCircle,
  Globe,
  WifiOff,
  RefreshCw,
  Mail,
  UserPlus,
  LogOut,
  ChevronDown,
  Radio,
} from 'lucide-react';
import {
  Room,
  RoomState,
  User as UserType,
  RoomMemberRole,
  BroadcastLink,
} from '../types';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import UpgradeModal from './UpgradeModal';
import EmojiPicker from './EmojiPicker';

interface RoomCardProps {
  room: Room;
  currentUser: UserType;
  onStateChange: (stateId: string) => void;
  onSettingsChange: (
    settings: Partial<Pick<Room, 'size' | 'viewMode'>>
  ) => void;
  onTimeoutSettingsChange: (settings: {
    autoResetState?: { stateId: string; afterMinutes: number };
  }) => void;
  onInviteUser: (email: string, role: RoomMemberRole, message?: string) => void;
  onAddUser?: (email: string, role: RoomMemberRole, message?: string) => void;
  onRemoveUser: (userId: string) => void;
  onUpdateUserRole: (userId: string, role: RoomMemberRole) => void;
  onCreateBroadcastLink: (options?: {
    name?: string;
    timeoutMinutes?: number;
    expiresAt?: Date;
  }) => void;
  onUpdateBroadcastLink: (
    linkId: string,
    updates: {
      name?: string;
      active?: boolean;
      timeoutMinutes?: number | null;
      expiresAt?: Date | null;
    }
  ) => void;
  onRevokeBroadcastLink: (linkId: string) => void;
  onDeleteBroadcastLink: (linkId: string) => void;
  onUpdateRoomStates: (states: RoomState[]) => void;
  onRefreshUserProfiles?: () => void;
  onLeaveRoom?: () => void;
}

const RoomCard: React.FC<RoomCardProps> = ({
  room,
  currentUser,
  onStateChange,
  onSettingsChange,
  onTimeoutSettingsChange,
  onInviteUser,
  onAddUser,
  onRemoveUser,
  onUpdateUserRole,
  onCreateBroadcastLink,
  onRevokeBroadcastLink,
  onDeleteBroadcastLink,
  onUpdateRoomStates,
  onRefreshUserProfiles,
  onLeaveRoom,
}) => {
  const { currentPlan, planLimits, checkLimit } = useSubscriptionLimits();
  const [showSettings, setShowSettings] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showStatesEditor, setShowStatesEditor] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<RoomMemberRole>('USER');
  const [inviteMessage, setInviteMessage] = useState('');
  const [editingStates, setEditingStates] = useState<RoomState[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState<string | null>(null);
  const [broadcastLinkName, setBroadcastLinkName] = useState('');
  const [broadcastTimeout, setBroadcastTimeout] = useState<number | ''>('');
  const [broadcastExpiry, setBroadcastExpiry] = useState('');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [refreshingProfiles, setRefreshingProfiles] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showStates, setShowStates] = useState(false);

  // Get current user's role in this room
  const currentUserRole =
    room.users.find((u) => u.id === currentUser.id)?.role || 'VIEWER';
  const canManageRoom = currentUserRole === 'ADMIN';
  const canChangeState =
    currentUserRole === 'ADMIN' || currentUserRole === 'USER';

  // Auto-refresh user profiles when component mounts or room changes
  useEffect(() => {
    if (onRefreshUserProfiles) {
      onRefreshUserProfiles();
    }
  }, [room.id, onRefreshUserProfiles]);

  // Generate user avatar with better error handling
  const generateUserAvatar = (user: any) => {
    const hasValidAvatar =
      user.avatar &&
      typeof user.avatar === 'string' &&
      user.avatar.trim() !== '' &&
      !imageErrors.has(user.id);

    const handleImageError = (userId: string) => {
      setImageErrors((prev) => new Set([...prev, userId]));
    };

    const handleImageLoad = (userId: string) => {
      setImageErrors((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    };

    if (hasValidAvatar) {
      return (
        <div className="relative w-8 h-8">
          <img
            src={user.avatar}
            alt={user.name}
            className="w-8 h-8 rounded-full object-cover border-2 border-white dark:border-slate-700 shadow-sm"
            onError={() => handleImageError(user.id)}
            onLoad={() => handleImageLoad(user.id)}
            style={{ display: imageErrors.has(user.id) ? 'none' : 'block' }}
          />
          {imageErrors.has(user.id) && (
            <div
              className={`w-8 h-8 rounded-full ${getAvatarColor(
                user.name
              )} flex items-center justify-center text-white text-sm font-semibold border-2 border-white dark:border-slate-700 shadow-sm absolute inset-0`}
            >
              {getInitials(user.name)}
            </div>
          )}
        </div>
      );
    }

    // Generate initials avatar
    return (
      <div
        className={`w-8 h-8 rounded-full ${getAvatarColor(
          user.name
        )} flex items-center justify-center text-white text-sm font-semibold border-2 border-white dark:border-slate-700 shadow-sm`}
      >
        {getInitials(user.name)}
      </div>
    );
  };

  // Helper function to get initials
  const getInitials = (name: string): string => {
    if (!name || typeof name !== 'string') return 'U';
    return (
      name
        .split(' ')
        .map((n) => n.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'U'
    );
  };

  // Helper function to get consistent avatar color
  const getAvatarColor = (name: string): string => {
    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-pink-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500',
      'bg-lime-500',
      'bg-rose-500',
    ];

    if (!name || typeof name !== 'string') return colors[0];

    const charCode = name.charCodeAt(0) || 0;
    return colors[charCode % colors.length];
  };

  // Get role icon
  const getRoleIcon = (role: RoomMemberRole) => {
    switch (role) {
      case 'ADMIN':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'USER':
        return <UserCheck className="w-4 h-4 text-blue-500" />;
      case 'VIEWER':
        return <Eye className="w-4 h-4 text-slate-400" />;
      default:
        return <User className="w-4 h-4 text-slate-400" />;
    }
  };

  // Get role color
  const getRoleColor = (role: RoomMemberRole) => {
    switch (role) {
      case 'ADMIN':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      case 'USER':
        return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20';
      case 'VIEWER':
        return 'text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50';
      default:
        return 'text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50';
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleRefreshProfiles = async () => {
    if (!onRefreshUserProfiles) return;
    if (refreshingProfiles) return;

    setRefreshingProfiles(true);
    try {
      // Clear image errors when refreshing
      setImageErrors(new Set());
      await onRefreshUserProfiles();
      showMessage('success', 'User profiles refreshed');
    } catch (error) {
      showMessage('error', 'Failed to refresh profiles');
    } finally {
      setRefreshingProfiles(false);
    }
  };

  const handleInviteUser = async () => {
    if (!newUserEmail.trim()) return;

    // Check participant limit
    const { allowed, reason } = await checkLimit(
      'participants',
      room.users.length
    );

    if (!allowed) {
      setUpgradeReason(
        reason || 'You have reached the participant limit for this room.'
      );
      setShowUpgradeModal(true);
      return;
    }

    try {
      // Use onInviteUser if available, otherwise fall back to onAddUser
      const inviteFunction = onInviteUser || onAddUser;
      if (inviteFunction) {
        await inviteFunction(
          newUserEmail.trim(),
          newUserRole,
          inviteMessage.trim() || undefined
        );
      }
      setNewUserEmail('');
      setNewUserRole('USER');
      setInviteMessage('');
      showMessage('success', 'Invitation sent successfully');
    } catch (error) {
      showMessage('error', 'Failed to send invitation');
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (confirm('Are you sure you want to remove this user?')) {
      try {
        await onRemoveUser(userId);
        showMessage('success', 'User removed successfully');
      } catch (error) {
        showMessage('error', 'Failed to remove user');
      }
    }
  };

  const handleUpdateUserRole = async (userId: string, role: RoomMemberRole) => {
    try {
      await onUpdateUserRole(userId, role);
      showMessage('success', 'User role updated successfully');
    } catch (error) {
      showMessage('error', 'Failed to update user role');
    }
  };

  const initializeStatesEditor = () => {
    // Create a deep copy of the room states to avoid mutations
    const statesCopy = room.states.map((state) => ({
      id: state.id,
      name: state.name,
      color: state.color,
      emoji: state.emoji,
    }));
    setEditingStates(statesCopy);
    setShowStatesEditor(true);
  };

  const addNewState = () => {
    // Generate a unique temporary ID that won't conflict with existing IDs
    const tempId = `temp_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    const newState: RoomState = {
      id: tempId,
      name: '',
      color: generateRandomColor(),
      emoji: '⚪',
    };

    setEditingStates((prevStates) => [...prevStates, newState]);
  };

  const updateEditingState = (id: string, updates: Partial<RoomState>) => {
    setEditingStates((prevStates) =>
      prevStates.map((state) =>
        state.id === id ? { ...state, ...updates } : state
      )
    );
  };

  const removeEditingState = (id: string) => {
    if (editingStates.length > 1) {
      setEditingStates((prevStates) =>
        prevStates.filter((state) => state.id !== id)
      );
    }
  };

  const generateRandomColor = () => {
    const colors = [
      '#ef4444',
      '#f97316',
      '#f59e0b',
      '#eab308',
      '#84cc16',
      '#22c55e',
      '#10b981',
      '#14b8a6',
      '#06b6d4',
      '#0ea5e9',
      '#3b82f6',
      '#6366f1',
      '#8b5cf6',
      '#a855f7',
      '#d946ef',
      '#ec4899',
      '#f43f5e',
      '#64748b',
      '#6b7280',
      '#374151',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const saveStates = async () => {
    if (isSaving) return; // Prevent multiple simultaneous saves

    setIsSaving(true);

    try {
      // Filter out empty states
      const validStates = editingStates.filter((state) => state.name.trim());

      if (validStates.length === 0) {
        showMessage('error', 'At least one state is required');
        return;
      }

      await onUpdateRoomStates(validStates);
      setShowStatesEditor(false);
      showMessage('success', 'States updated successfully');
    } catch (error) {
      console.error('Failed to save states:', error);
      showMessage('error', 'Failed to update states');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateBroadcastLink = async () => {
    // Check broadcast link limit
    const { allowed, reason } = await checkLimit(
      'broadcastLinks',
      room.broadcastLinks.length
    );

    if (!allowed) {
      setUpgradeReason(
        reason || 'You have reached the broadcast link limit for this room.'
      );
      setShowUpgradeModal(true);
      return;
    }

    const options: any = {};

    if (broadcastLinkName.trim()) {
      options.name = broadcastLinkName.trim();
    }

    if (broadcastTimeout && typeof broadcastTimeout === 'number') {
      options.timeoutMinutes = broadcastTimeout;
    }

    if (broadcastExpiry) {
      options.expiresAt = new Date(broadcastExpiry);
    }

    try {
      await onCreateBroadcastLink(options);
      setBroadcastLinkName('');
      setBroadcastTimeout('');
      setBroadcastExpiry('');
      showMessage('success', 'Broadcast link created successfully');
    } catch (error) {
      showMessage('error', 'Failed to create broadcast link');
    }
  };

  const copyBroadcastLink = (linkId: string) => {
    const url = `${window.location.origin}/broadcast/${room.id}/${linkId}`;
    navigator.clipboard.writeText(url);
    showMessage('success', 'Link copied to clipboard');
  };

  const formatTimestamp = (timestamp: number | Date) => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days === 1 ? '' : 's'} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else {
      return 'just now';
    }
  };

  const isLinkExpired = (link: BroadcastLink) => {
    const now = Date.now();

    if (link.expiresAt && link.expiresAt <= now) {
      return true;
    }

    if (link.timeoutMinutes) {
      const timeoutMs = link.timeoutMinutes * 60 * 1000;
      if (link.createdAt + timeoutMs <= now) {
        return true;
      }
    }

    return false;
  };

  const isUserAdmin = () => {
    if (!currentUser) return false;
    return room.users.find(u => u.id === currentUser.id)?.role === 'ADMIN';
  };

  const handleLeaveRoom = async () => {
    if (!currentUser) return;
    
    const isLastUser = room.users.length === 1;
    
    if (isUserAdmin() && isLastUser) {
      if (window.confirm('You are the last admin in this room. Leaving will delete the room. Are you sure?')) {
        await onLeaveRoom?.();
      }
    } else {
      await onLeaveRoom?.();
    }
  };

  return (
    <div className="relative bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
      {/* Message Display */}
      {message && (
        <div className={`absolute top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Room Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {room.name}
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowMembers(!showMembers)}
              className={`p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors ${
                showMembers ? 'bg-slate-100 dark:bg-slate-700' : ''
              }`}
              title="Members"
            >
              <Users className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            </button>
            <button
              onClick={() => setShowBroadcast(!showBroadcast)}
              className={`p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors ${
                showBroadcast ? 'bg-slate-100 dark:bg-slate-700' : ''
              }`}
              title="Broadcast Links"
            >
              <Globe className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            </button>
            {canManageRoom && (
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors ${
                  showSettings ? 'bg-slate-100 dark:bg-slate-700' : ''
                }`}
                title="Settings"
              >
                <Settings className="w-4 h-4 text-slate-600 dark:text-slate-300" />
              </button>
            )}
            {/* Leave Room Button */}
            {currentUser && (
              <button
                onClick={handleLeaveRoom}
                className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                title={isUserAdmin() && room.users.length === 1 ? "Leave and delete room" : "Leave room"}
              >
                <LogOut className="w-4 h-4 text-red-600 dark:text-red-400" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div 
        className="p-4 relative flex flex-col items-center justify-center min-h-[160px]"
        style={{ 
          backgroundColor: room.currentState.color,
          opacity: 0.95
        }}
      >
        {/* Current State Display */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-white font-medium shadow-lg"
            style={{ backgroundColor: room.currentState.color }}
          >
            {room.currentState.emoji && (
              <span className="text-2xl">{room.currentState.emoji}</span>
            )}
            <span className="text-lg">{room.currentState.name}</span>
          </div>
          <div className="text-xs text-white/60">
            {formatTimestamp(room.lastUpdated)}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Section */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            <div className="flex -space-x-2">
              {room.users.slice(0, 3).map((user) => (
                <div
                  key={user.id}
                  className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center border-2 border-white dark:border-slate-800 overflow-hidden"
                >
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xs text-slate-600 dark:text-slate-300">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              ))}
              {room.users.length > 3 && (
                <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center border-2 border-white dark:border-slate-800">
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    +{room.users.length - 3}
                  </span>
                </div>
              )}
            </div>
          </div>
          {canChangeState && (
            <button
              onClick={() => setShowStates(!showStates)}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title={showStates ? 'Hide states' : 'Show states'}
            >
              <ChevronDown
                className={`w-5 h-5 text-slate-600 dark:text-slate-300 transition-transform ${
                  showStates ? 'rotate-180' : ''
                }`}
              />
            </button>
          )}
        </div>
      </div>

      {/* States Section */}
      {showStates && (
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="grid grid-cols-2 gap-2">
            {room.states.map((state) => (
              <button
                key={state.id}
                onClick={() => {
                  onStateChange(state.id);
                }}
                disabled={state.id === room.currentState.id}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg text-white font-medium transition-all ${
                  state.id === room.currentState.id
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-white/20'
                }`}
                style={{ backgroundColor: state.color }}
                title={state.name}
              >
                {state.emoji && (
                  <span className="text-xl flex-shrink-0">{state.emoji}</span>
                )}
                <span className="text-sm truncate">{state.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Members Panel */}
      {showMembers && (
        <div className="p-4 sm:p-6 border-b border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-700/20">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Members ({room.users.length})
              {planLimits && planLimits.maxParticipantsPerRoom !== -1 && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  / {planLimits.maxParticipantsPerRoom} max
                </span>
              )}
            </h4>
          </div>

          {/* Plan Limit Warning */}
          {planLimits &&
            currentPlan === 'free' &&
            room.users.length >= planLimits.maxParticipantsPerRoom - 2 && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-medium text-amber-800 dark:text-amber-300">
                      Approaching participant limit
                    </p>
                    <p className="text-amber-700 dark:text-amber-400 mt-1">
                      Upgrade to add more participants to this room.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Members List */}
          <div className="space-y-3 mb-4">
            {room.users.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between p-3 bg-white/60 dark:bg-slate-800/60 rounded-xl border border-slate-200/50 dark:border-slate-700/50"
              >
                <div className="flex items-center gap-3">
                  {generateUserAvatar(user)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                      {user.name}
                      {user.id === currentUser.id && (
                        <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
                          (You)
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {user.email}
                    </p>
                  </div>
                  <div
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${getRoleColor(
                      user.role
                    )}`}
                  >
                    {getRoleIcon(user.role)}
                    <span>{user.role}</span>
                  </div>
                </div>

                {canManageRoom && user.id !== currentUser.id && (
                  <div className="flex items-center gap-1 ml-2">
                    <select
                      value={user.role}
                      onChange={(e) =>
                        handleUpdateUserRole(
                          user.id,
                          e.target.value as RoomMemberRole
                        )
                      }
                      className="text-xs px-2 py-1 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    >
                      <option value="ADMIN">Admin</option>
                      <option value="USER">User</option>
                      <option value="VIEWER">Viewer</option>
                    </select>
                    <button
                      onClick={() => handleRemoveUser(user.id)}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 transition-colors"
                      title="Remove user"
                    >
                      <UserX className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Invite User Form */}
          {canManageRoom && (
            <div className="space-y-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50">
              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Invite Member
              </h5>
              <div className="space-y-2">
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
                <div className="flex gap-2">
                  <select
                    value={newUserRole}
                    onChange={(e) =>
                      setNewUserRole(e.target.value as RoomMemberRole)
                    }
                    className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  >
                    <option value="USER">User</option>
                    <option value="ADMIN">Admin</option>
                    <option value="VIEWER">Viewer</option>
                  </select>
                  <button
                    onClick={handleInviteUser}
                    disabled={!newUserEmail.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  >
                    <UserPlus className="w-4 h-4" />
                    Invite
                  </button>
                </div>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder="Optional invitation message..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 resize-none"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Broadcast Links Panel */}
      {showBroadcast && (
        <div className="p-4 sm:p-6 border-b border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-700/20">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Broadcast Links
              {planLimits && planLimits.maxBroadcastLinksPerRoom !== -1 && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  ({room.broadcastLinks.length}/
                  {planLimits.maxBroadcastLinksPerRoom})
                </span>
              )}
            </h4>
          </div>

          {/* Plan Limit Warning */}
          {planLimits &&
            currentPlan === 'free' &&
            room.broadcastLinks.length >=
              planLimits.maxBroadcastLinksPerRoom && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-medium text-amber-800 dark:text-amber-300">
                      Broadcast link limit reached
                    </p>
                    <p className="text-amber-700 dark:text-amber-400 mt-1">
                      Upgrade to create more broadcast links for this room.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Existing Links */}
          <div className="space-y-3 mb-4">
            {room.broadcastLinks.map((link) => {
              const expired = isLinkExpired(link);
              return (
                <div
                  key={link.id}
                  className="p-3 bg-white/60 dark:bg-slate-800/60 rounded-xl border border-slate-200/50 dark:border-slate-700/50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          link.active && !expired
                            ? 'bg-green-500'
                            : 'bg-red-500'
                        }`}
                      />
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                        {link.name}
                      </span>
                      {expired && (
                        <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
                          Expired
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyBroadcastLink(link.id)}
                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-500 dark:text-slate-400 transition-colors"
                        title="Copy link"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <a
                        href={`/broadcast/${room.id}/${link.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-500 dark:text-slate-400 transition-colors"
                        title="Open link"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      {canManageRoom && (
                        <>
                          <button
                            onClick={() => onRevokeBroadcastLink(link.id)}
                            className="p-1 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded text-yellow-500 transition-colors"
                            title="Deactivate link"
                          >
                            <WifiOff className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => onDeleteBroadcastLink(link.id)}
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 transition-colors"
                            title="Delete link"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Created: {formatTimestamp(link.createdAt)}
                    {link.expiresAt && (
                      <span className="ml-2">
                        • Expires: {formatTimestamp(link.expiresAt)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Create New Link */}
          {canManageRoom && (
            <div className="space-y-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50">
              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Create New Link
              </h5>
              <div className="space-y-2">
                <input
                  type="text"
                  value={broadcastLinkName}
                  onChange={(e) => setBroadcastLinkName(e.target.value)}
                  placeholder="Link name (optional)"
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    value={broadcastTimeout}
                    onChange={(e) =>
                      setBroadcastTimeout(
                        e.target.value ? Number(e.target.value) : ''
                      )
                    }
                    placeholder="Timeout (minutes)"
                    className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  />
                  <input
                    type="datetime-local"
                    value={broadcastExpiry}
                    onChange={(e) => setBroadcastExpiry(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <button
                  onClick={handleCreateBroadcastLink}
                  className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                >
                  Create Link
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && canManageRoom && (
        <div className="p-4 sm:p-6 bg-slate-50/50 dark:bg-slate-700/20">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Room Settings
            </h4>
          </div>

          <div className="space-y-4">
            {/* Room Display Settings */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Card Size
                </label>
                <select
                  value={room.size}
                  onChange={(e) =>
                    onSettingsChange({ size: e.target.value as any })
                  }
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  View Mode
                </label>
                <select
                  value={room.viewMode}
                  onChange={(e) =>
                    onSettingsChange({ viewMode: e.target.value as any })
                  }
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                >
                  <option value="minimal">Minimal</option>
                  <option value="detailed">Detailed</option>
                </select>
              </div>
            </div>

            {/* States Management */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Room States
                </label>
                <button
                  onClick={initializeStatesEditor}
                  className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Edit States
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {room.states.map((state) => (
                  <div
                    key={state.id}
                    className="flex items-center gap-2 p-2 bg-white/60 dark:bg-slate-800/60 rounded-lg border border-slate-200/50 dark:border-slate-700/50"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: state.color }}
                    />
                    {state.emoji && (
                      <span className="text-sm">{state.emoji}</span>
                    )}
                    <span className="text-xs text-slate-700 dark:text-slate-300 truncate">
                      {state.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Auto Reset Settings */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="enableAutoReset"
                  checked={!!room.autoResetState}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onTimeoutSettingsChange({
                        autoResetState: {
                          stateId: room.states[0].id,
                          afterMinutes: 60,
                        },
                      });
                    } else {
                      onTimeoutSettingsChange({});
                    }
                  }}
                  className="rounded border-slate-300 dark:border-slate-600 text-blue-500 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
                <label
                  htmlFor="enableAutoReset"
                  className="text-xs font-medium text-slate-700 dark:text-slate-300"
                >
                  Auto Reset
                </label>
              </div>

              {room.autoResetState && (
                <div className="grid grid-cols-2 gap-2 pl-6">
                  <select
                    value={room.autoResetState.stateId}
                    onChange={(e) =>
                      onTimeoutSettingsChange({
                        autoResetState: {
                          stateId: e.target.value,
                          afterMinutes: room.autoResetState!.afterMinutes,
                        },
                      })
                    }
                    className="px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  >
                    {room.states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.emoji} {state.name}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="1"
                      value={room.autoResetState.afterMinutes}
                      onChange={(e) =>
                        onTimeoutSettingsChange({
                          autoResetState: {
                            stateId: room.autoResetState!.stateId,
                            afterMinutes: Number(e.target.value),
                          },
                        })
                      }
                      className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    />
                    <Timer className="w-3 h-3 text-slate-400" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* States Editor Modal */}
      {showStatesEditor && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">
                  Edit Room States
                </h3>
                <button
                  onClick={() => setShowStatesEditor(false)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {editingStates.map((state, index) => (
                <div
                  key={state.id}
                  className="flex gap-3 items-center p-3 bg-slate-50 dark:bg-slate-700 rounded-xl"
                >
                  <input
                    type="text"
                    value={state.name}
                    onChange={(e) =>
                      updateEditingState(state.id, { name: e.target.value })
                    }
                    placeholder="State name"
                    className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    required
                  />
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setShowEmojiPicker(
                          showEmojiPicker === state.id ? null : state.id
                        )
                      }
                      className="w-10 h-10 px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-center text-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                      title="Select emoji"
                    >
                      {state.emoji || '⚪'}
                    </button>
                    {showEmojiPicker === state.id && (
                      <div className="absolute top-full left-0 mt-1 z-50">
                        <EmojiPicker
                          onEmojiSelect={(emoji) => {
                            updateEditingState(state.id, { emoji });
                            setShowEmojiPicker(null);
                          }}
                          onClose={() => setShowEmojiPicker(null)}
                        />
                      </div>
                    )}
                  </div>
                  <input
                    type="color"
                    value={state.color}
                    onChange={(e) =>
                      updateEditingState(state.id, { color: e.target.value })
                    }
                    className="w-10 h-10 rounded-lg cursor-pointer border border-slate-300 dark:border-slate-600"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      updateEditingState(state.id, {
                        color: generateRandomColor(),
                      })
                    }
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                    title="Random color"
                  >
                    <Shuffle className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  </button>
                  {editingStates.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeEditingState(state.id)}
                      className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      title="Remove state"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addNewState}
                className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl hover:border-blue-400 dark:hover:border-blue-500 transition-colors text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
              >
                <Plus className="w-4 h-4" />
                <span>Add State</span>
              </button>
            </div>

            <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowStatesEditor(false)}
                className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveStates}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save States'}
              </button>
            </div>
          </div>
        </div>
      )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason={upgradeReason}
        currentPlan={currentPlan}
        suggestedPlan={currentPlan === 'free' ? 'pro' : 'enterprise'}
      />
    </div>
  );
};

export default RoomCard;